<template>
  <div>
    <b-card>
      <b-row>
        <b-col
          v-if="hasPermission('privacy-residual-risk', 'create')"
          md="3"
          class="mb-1"
        >
          <b-button
            variant="outline-primary"
            :to="{ name: 'privacy-residual-risk-add' }"
          >
            <feather-icon
              icon="PlusIcon"
              class="mr-50"
            />
            {{ $t('Agregar ARR') }}
          </b-button>
        </b-col>

        <b-col
          v-if="hasPermission('privacy-residual-risk', 'export')"
          md="2"
          class="mb-1"
        >
          <b-button
            variant="outline-success"
            :disabled="!analyses.length"
            @click="handleExportExcel"
          >
            <feather-icon
              icon="DownloadIcon"
              class="mr-50"
            />
            {{ $t('Export Excel') }}
          </b-button>
        </b-col>

        <b-col md="2" class="mb-1">
          <b-button
            variant="outline-secondary"
            :disabled="clearFiltersDisabled"
            @click="clearFilters"
          >
            <feather-icon
              icon="TrashIcon"
              class="mr-50"
            />
            {{ $t('clear_filters') }}
          </b-button>
        </b-col>

        <b-col md="5">
          <b-form-group>
            <b-form-input
              v-model="searchQuery"
              :placeholder="$t('message.search')"
              type="search"
              class="d-inline-block"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col>
          <filters
            :levelFilterShow="true"
            :decisionFilterShow="true"
            :statusFilterShow="true"
            @changeFilter="updateFilter"
          />
        </b-col>
      </b-row>

      <b-row v-if="analyses.length > 0" class="mb-2">
        <b-col cols="12">
          <b-card class="mb-0">
            <div class="d-flex align-items-center justify-content-center">
              <div>
                <h5 class="mb-0">
                  <feather-icon icon="TrendingDownIcon" size="20" class="mr-50" />
                  Promedio de Reducción de Riesgo
                </h5>
                <small class="text-muted">
                  {{ totalAnalyses }} tratamiento{{ totalAnalyses !== 1 ? 's' : '' }}
                </small>
              </div>
              <div class="text-right ml-2">
                <h2 class="mb-0" :class="averageReductionClass">
                  <feather-icon :icon="averageReductionIcon" size="24" />
                  {{ averageReduction }}%
                </h2>
                <b-badge :variant="averageReductionVariant" class="mt-50">
                  {{ averageReductionLabel }}
                </b-badge>
              </div>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12">
          <b-table
            ref="refARRTable"
            :items="analyses"
            hover
            responsive
            :fields="tableColumns"
            primary-key="_id"
            :sort-by.sync="sortBy"
            show-empty=""
            :empty-text="isLoading ? $t('message.loading') : $t('message.no_records')"
            :sort-desc.sync="isSortDirDesc"
            @row-clicked="handleRowClick"
            tbody-tr-class="cursor-pointer"
          >
            <!-- ID Tratamiento -->
            <template #cell(treatmentId)="data">
              <span class="font-weight-bold">{{ data.item.treatmentId }}</span>
            </template>

            <!-- Nombre Tratamiento -->
            <template #cell(treatmentName)="data">
              <span>{{ data.item.treatmentName }}</span>
            </template>

            <!-- P_inh -->
            <template #cell(inheritedP)="data">
              <b-badge variant="light-primary" class="font-weight-bold">
                {{ data.item.inheritedP || 'N/A' }}
              </b-badge>
            </template>

            <!-- I_inh -->
            <template #cell(inheritedI)="data">
              <b-badge variant="light-info" class="font-weight-bold">
                {{ data.item.inheritedI || 'N/A' }}
              </b-badge>
            </template>

            <!-- Riesgo Inherente -->
            <template #cell(inheritedRisk)="data">
              <b-badge variant="light-dark" class="font-weight-bold">
                {{ data.item.inheritedRisk || 'N/A' }}
              </b-badge>
            </template>

            <!-- Nivel Inherente -->
            <template #cell(inheritedLevel)="data">
              <risk-level-badge :level="data.item.inheritedLevel || ''" />
            </template>

            <!-- P_res -->
            <template #cell(residualP)="data">
              <b-badge variant="light-primary" class="font-weight-bold">
                {{ data.item.residualP || 'N/A' }}
              </b-badge>
            </template>

            <!-- I_res -->
            <template #cell(residualI)="data">
              <b-badge variant="light-info" class="font-weight-bold">
                {{ data.item.residualI || 'N/A' }}
              </b-badge>
            </template>

            <!-- Riesgo Residual -->
            <template #cell(residualRisk)="data">
              <b-badge variant="light-dark" class="font-weight-bold">
                {{ data.item.residualRisk || 'N/A' }}
              </b-badge>
            </template>

            <!-- Nivel Residual -->
            <template #cell(residualLevel)="data">
              <risk-level-badge :level="data.item.residualLevel || ''" />
            </template>

            <!-- Reducción % -->
            <template #cell(riskReductionPct)="data">
              <reduction-indicator :reductionPct="data.item.riskReductionPct || 0" />
            </template>

            <!-- Decisión -->
            <template #cell(decision)="data">
              <b-badge
                :variant="getDecisionVariant(data.item.decision)"
                class="font-weight-bold"
              >
                {{ data.item.decision || 'N/A' }}
              </b-badge>
            </template>

            <!-- Estado Plan -->
            <template #cell(status)="data">
              <b-badge
                :variant="getStatusVariant(data.item.status)"
                class="font-weight-bold"
              >
                {{ data.item.status || 'N/A' }}
              </b-badge>
            </template>

            <!-- Acciones -->
            <template #cell(actions)="data">
              <div class="flex flex-column">
                <EditButton
                  v-if="hasPermission('privacy-residual-risk', 'edit')"
                  @clicked="router.push({name: 'privacy-residual-risk-edit', params: { id: data.item._id }})"
                />
                <DeleteButton
                  v-if="hasPermission('privacy-residual-risk', 'delete')"
                  v-b-modal="`delete modal ${data.item._id}`"
                />
                <b-modal
                  :id="`delete modal ${data.item._id}`"
                  centered
                  no-close-on-backdrop
                  modal-class="modal-danger"
                  ok-variant="danger"
                  cancel-variant="outline-secondary"
                  :title="$t('message.confirm_action')"
                  :ok-title="$t('message.delete')"
                  :cancel-title="$t('message.cancel')"
                  @ok="deleteAnalysis(data.item)"
                >
                  <span>{{ $tc('message.confirm_delete_arr', 0, { treatment: data.item.treatmentName }) }}</span>
                </b-modal>
              </div>
            </template>
          </b-table>
        </b-col>

        <b-col
          cols="12"
          class="d-flex justify-content-between flex-wrap"
        >
          <div class="mb-1">
            <b-form-group class="mb-0">
              <label class="d-inline-block text-sm-left mr-50">{{ $t('message.per_page') }}</label>
              <b-form-select
                id="perPageSelect"
                v-model="perPage"
                size="sm"
                :options="[10, 25, 50, 100]"
                class="per-page-selector"
              />
            </b-form-group>
          </div>

          <div class="mb-1">
            <span class="text-muted">{{ $tc('message.paginationText', 0, { from: `${((currentPage * perPage) - perPage) + (analyses.length ? 1 : 0)}`, to: `${analyses.length + ((currentPage * perPage) - perPage)}`, total: `${totalAnalyses}` }) }}</span>
          </div>

          <b-pagination
            v-model="currentPage"
            :total-rows="totalAnalyses"
            :per-page="perPage"
            first-number=""
            last-number=""
            prev-class="prev-item"
            next-class="next-item"
          />
        </b-col>
      </b-row>
    </b-card>
  </div>
</template>

<script>
import { BBadge, BPagination, BTable, BModal, VBModal } from 'bootstrap-vue'
import { onMounted, computed } from '@vue/composition-api'
import { useRouter } from '@core/utils/utils'
import { hasPermission } from '@/composables/usePermission'
import EditButton from '@/views/components/Shared/Buttons/EditButton.vue'
import DeleteButton from '@/views/components/Shared/Buttons/DeleteButton.vue'
import Filters from '@/views/organization/Filters.vue'
import RiskLevelBadge from '../../components/RiskLevelBadge.vue'
import ReductionIndicator from '../../components/ReductionIndicator.vue'
import useResidualRiskList from './useResidualRiskList'
import useExportARR from './useExportARR'
import Ripple from 'vue-ripple-directive'

export default {
  name: 'ResidualRiskList',
  components: {
    BTable,
    BBadge,
    BPagination,
    EditButton,
    DeleteButton,
    Filters,
    BModal,
    RiskLevelBadge,
    ReductionIndicator,
  },
  directives: {
    'b-modal': VBModal,
    Ripple,
  },
  setup() {
    const { router } = useRouter()

    const {
      fetchAnalyses,
      tableColumns,
      perPage,
      currentPage,
      totalAnalyses,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refARRTable,
      analyses,
      deleteAnalysis,
      isLoading,
      clearFilters,
      clearFiltersDisabled,
      updateFilter,
      fetchAllAnalysesForExport,
    } = useResidualRiskList()

    const { exportToExcel } = useExportARR()

    const getDecisionVariant = (decision) => {
      const variants = {
        'Aceptar': 'success',
        'Mitigar': 'warning',
        'Transferir': 'info',
        'Evitar': 'danger',
      }
      return variants[decision] || 'secondary'
    }

    const getStatusVariant = (status) => {
      const variants = {
        'Pendiente': 'warning',
        'En progreso': 'info',
        'Cerrado': 'success',
      }
      return variants[status] || 'secondary'
    }

    const handleExportExcel = async () => {
      if (!hasPermission('privacy-residual-risk', 'export')) return
      try {
        // Fetch all analyses respecting current filters
        const allAnalyses = await fetchAllAnalysesForExport()
        exportToExcel(allAnalyses)
      } catch (error) {
        console.log(error)
      }
    }

    const handleRowClick = (item) => {
      router.push({ name: 'privacy-residual-risk-edit', params: { id: item._id } })
    }

    // Calculate average reduction from all visible analyses
    const averageReduction = computed(() => {
      if (!analyses.value || analyses.value.length === 0) return 0

      const total = analyses.value.reduce((sum, analysis) => {
        return sum + (analysis.riskReductionPct || 0)
      }, 0)

      return Math.round(total / analyses.value.length)
    })

    const averageReductionVariant = computed(() => {
      const pct = averageReduction.value
      if (pct >= 50) return 'success'
      if (pct >= 25) return 'warning'
      if (pct > 0) return 'info'
      return 'secondary'
    })

    const averageReductionClass = computed(() => {
      const pct = averageReduction.value
      if (pct >= 50) return 'text-success'
      if (pct >= 25) return 'text-warning'
      if (pct > 0) return 'text-info'
      return 'text-secondary'
    })

    const averageReductionIcon = computed(() => {
      const pct = averageReduction.value
      if (pct >= 50) return 'TrendingDownIcon'
      if (pct > 0) return 'ArrowDownIcon'
      return 'MinusIcon'
    })

    const averageReductionLabel = computed(() => {
      const pct = averageReduction.value
      if (pct >= 50) return 'Reducción Alta'
      if (pct >= 25) return 'Reducción Media'
      if (pct > 0) return 'Reducción Baja'
      return 'Sin Reducción'
    })

    onMounted(() => {
      fetchAnalyses()
    })

    return {
      router,
      fetchAnalyses,
      tableColumns,
      perPage,
      currentPage,
      totalAnalyses,
      searchQuery,
      sortBy,
      isSortDirDesc,
      deleteAnalysis,
      refARRTable,
      analyses,
      isLoading,
      clearFilters,
      clearFiltersDisabled,
      hasPermission,
      updateFilter,
      handleExportExcel,
      getDecisionVariant,
      getStatusVariant,
      handleRowClick,
      averageReduction,
      averageReductionVariant,
      averageReductionClass,
      averageReductionIcon,
      averageReductionLabel,
    }
  },
}
</script>

<style lang="scss" scoped>
.form-group {
  label {
    font-size: 0.85rem;
    font-weight: 400;
  }
}

.per-page-selector {
  width: 90px;
}

::v-deep .cursor-pointer {
  cursor: pointer;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
